import { ArrowDropUpRounded, ContentCopyRounded, ContentPasteRounded, DeleteRounded, LanguageRounded, LinearScaleRounded, ShapeLineRounded, TimelineRounded, UpdateRounded } from '@mui/icons-material'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import EditIcon from '@mui/icons-material/Edit'
import LogoutIcon from '@mui/icons-material/Logout'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { LoadingButton } from '@mui/lab'
import { Button, ButtonGroup, CircularProgress, Divider, Menu, MenuItem, Modal, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useKeyPress, Node as XYNode } from '@xyflow/react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../../backend/ApiClient'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { IApprovalRequest } from '../../../definitions/Approval'
import { IFlow, INode } from '../../../definitions/Nodes'
import { IExecutionEnvironment, IFlowRunConfig, ISimpleExecutionEnvironment } from '../../../definitions/NodesExecution'
import EventsEngine from '../../../engines/EventsEngine'
import Helper from '../../../helpers/Helper'
import FlowUIManager, { IFlowChange } from '../../../managers/FlowUIManager'
import { useAppSelector } from '../../../store/Hooks'
import { changeRoute, overwriteRouteParams } from '../../../store/slices/SessionSlice'
import useCommand, { CommandStatus } from '../../hooks/useCommand'
import useInvalidate from '../../hooks/useInvalidate'
import { buildQueryKey } from '../../hooks/useQuery'
import SummaryExecEnvFlowComparison from '../../routes/SummaryExecEnvComparison'
import { snackbar } from '../../services/SnackbarService'
import ExecuteStatusModal from '../ExecuteStatusModal'
import FlowSettings from '../FlowSettings'
import SetNodeGlobalsModal from '../SetNodeGlobalsModal'
import { FlowEditorMode } from '../SettlementFlowEditor'
import QuantumTimelineUI from '../timeline/QuantumTimelineUI'
import ExecuteFlowButton from './ExecuteFlowButton'
import UpdateFlowModal from './UpdateFlowModal'

interface P {
    editorMode: FlowEditorMode
    flow: IFlow
    setFlow: (flow: IFlow) => void
    nodes: XYNode[]
    execEnv: IExecutionEnvironment
    comparedExecEnv: ISimpleExecutionEnvironment
    autoLayout: () => void
    updateFlow: (change: IFlowChange) => void
    applyFlowChange: (change: IFlowChange) => void
    setExecEnv: (newExecEnv: IExecutionEnvironment) => void
    setComparedExecEnv: (comparedEE: ISimpleExecutionEnvironment) => void
    setEditorMode: (mode: FlowEditorMode) => void
    statusUpdateFlow: CommandStatus
    flowRunConfig?: IFlowRunConfig
    approval: IApprovalRequest
}

function FlowFloatingActionBar({ flow, setFlow, editorMode, nodes, execEnv, comparedExecEnv, statusUpdateFlow, updateFlow, autoLayout, applyFlowChange, setExecEnv, setEditorMode, setComparedExecEnv, flowRunConfig, approval }: P) {
    const theme = useTheme()
    const dispatch = useDispatch()
    const [anchorElMenu, setAnchorElMenu] = useState<HTMLElement>(null)
    const openMenu = Boolean(anchorElMenu)
    const [openInfoModal, setOpenInfoModal] = useState(false)
    const [openSummaryComparison, setOpenSummaryComparison] = useState(false)
    const [openTimelineModal, setOpenTimelineModal] = useState(false)
    const [openGlobalsModal, setOpenGlobalsModal] = useState(false)
    const [openBatchUpdateModal, setOpenBatchUpdateModal] = useState(false)
    const [error, setError] = useState(null)
    const isDryRun = approval.status === 'draft' || approval.status === 'rejected' || approval.status === 'ready-for-review'
    const [runMode, setRunMode] = useState<'dry' | 'full'>(isDryRun ? 'dry' : 'full')
    const history = useAppSelector(s => s.session.routeHistory)
    const invalidateFlow = useInvalidate({ queryKey: buildQueryKey('get-flow-', flow.uuid) })
    const invalidateApproval = useInvalidate({ queryKey: buildQueryKey('get-approval-', flow.uuid) })
    const invalidateSubFlow = useInvalidate({ queryKey: buildQueryKey('get-all-subflows') })
    const canEdit = approval.status === 'draft' || approval.status === 'rejected'
    const isOldLive = approval.status === 'approved' && !flow.versioning.isHead
    const ctrlD = useKeyPress(['Control+d', 'Meta+d'])
    const isCompiled = flow.nodes.every(n => n?.jsonSchema)

    const { execute, status } = useCommand({
        commandFn: (selectedNodesId: string[]) => {
            const updatedFlow = ApiClient.Flow.createSubFlow(flow.uuid, selectedNodesId)
            return updatedFlow
        },
        onFinish: result => {
            if (result.status === 'success') {
                invalidateFlow()
                invalidateSubFlow()
                EventsEngine.publish('add-flow-tab', { flowId: result.response.uuid, name: result.response.name })
            } else snackbar.show({ severity: 'error', text: result.errorText })
        }
    })

    const { execute: getCurrentFlow, status: getCurrentFlowStatus } = useCommand({
        commandFn: async () => await FlowApiClient.getFlow(flow.uuid),
        onFinish: currentFlow => {
            if (currentFlow.status === 'success') {
                setEditorMode('edit')
                invalidateFlow()
                invalidateApproval()
                invalidateSubFlow()
                EventsEngine.publish('compile-error-style', { hasErrors: false, nodeIds: null })
                setExecEnv(null)
                dispatch(overwriteRouteParams({ flowId: currentFlow.response.uuid }))
            }
            if (currentFlow.status === 'error') {
                snackbar.show({ severity: 'error', text: currentFlow.errorText })
            }
        }
    })

    const { execute: compileFlow, status: statusCompileFlow } = useCommand({
        commandFn: async (flowId: string) => {
            const compiledExecEnv = ApiClient.Flow.compileFlow(flowId)
            EventsEngine.publish('compile-error-style', { hasErrors: false, nodeIds: null })
            setExecEnv(null)
            return compiledExecEnv
        },
        onFinish: async result => {
            if (result.status === 'error') {
                snackbar.show({ severity: 'error', text: `Generic error: ${result.errorText}` })
                const newFlow = await ApiClient.Flow.getFlow(flow.uuid)
                applyFlowChange({ flow: newFlow, isUpdated: true, needsSync: false })
            } else {
                if (result.response.status === 'completed') {
                    applyFlowChange({ flow: result.response.flow, isUpdated: true, needsSync: false })
                    snackbar.show({ severity: 'success', text: 'Flow is compiled successfully' })
                } else {
                    setExecEnv(result.response)
                    EventsEngine.publish('compile-error-style', { hasErrors: true, nodeIds: result.response.errors.map(e => e.nodeId) })
                    const newFlow = await ApiClient.Flow.getFlow(flow.uuid)
                    applyFlowChange({ flow: newFlow, isUpdated: true, needsSync: false })
                }
            }
        }
    })

    const copyInClipboard = async () => {
        const payload = { nodes: flow.nodes, globals: flow.globals }
        const text = JSON.stringify(payload)
        await navigator.clipboard.writeText(text)
        setAnchorElMenu(null)
        snackbar.show({ severity: 'success', text: 'Flow copied in the clipboard' })
    }

    useEffect(() => {
        if (ctrlD) onCopy()
    }, [ctrlD])

    useEffect(() => {
        if (flowRunConfig) {
            if (flowRunConfig?.dryRunConfig?.samplePercentage) {
                setRunMode('dry')
                return
            }
            setRunMode('full')
        }
    }, [flowRunConfig])

    const color = FlowUIManager.getActionBarColor(editorMode, runMode, flow)
    const selectedNodes = nodes.filter(x => x.selected === true)

    const renderMenu = () => {
        return (
            <div>
                <Tooltip title='Open menu' arrow>
                    <Button
                        id='basic-button'
                        color={color}
                        onClick={e => setAnchorElMenu(e.currentTarget)}
                        aria-controls={open ? 'basic-menu' : undefined}
                        aria-haspopup='true'
                        aria-expanded={open ? 'true' : undefined}>
                        <ArrowDropUpRounded />
                    </Button>
                </Tooltip>
                <Menu
                    disableAutoFocus
                    id='basic-menu'
                    variant='menu'
                    sx={{ top: -50, left: -30 }}
                    color={color}
                    anchorEl={anchorElMenu}
                    open={openMenu}
                    onClose={() => setAnchorElMenu(null)}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button'
                    }}>
                    <MenuItem color={color} onClick={autoLayout} disabled={!canEdit || editorMode !== 'edit'}>
                        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                            <LinearScaleRounded color='primary' />
                            <Typography variant='button' color={theme.palette.primary.main}>ORGANIZE</Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem color={color} onClick={() => setOpenTimelineModal(true)} disabled={error !== null}>
                        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                            <TimelineRounded color='primary' />
                            <Typography variant='button' color={theme.palette.primary.main}>TIMELINE</Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem onClick={() => setOpenGlobalsModal(true)}>
                        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                            <LanguageRounded color='primary' />
                            <Typography variant='button' color={theme.palette.primary.main}>GLOBALS</Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem onClick={() => setOpenBatchUpdateModal(true)} disabled={!canEdit || editorMode !== 'edit'}>
                        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                            <UpdateRounded color='primary' />
                            <Typography variant='button' color={theme.palette.primary.main}>Batch Update</Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem onClick={() => copyInClipboard()}>
                        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                            <ContentPasteRounded color='primary' />
                            <Typography variant='button' color={theme.palette.primary.main}>Copy in clipboard</Typography>
                        </Stack>
                    </MenuItem>
                    {Helper.isDev() && (
                        <div>
                            <Divider sx={{ color: 'red' }}>
                                <Typography variant='caption'>DEV ONLY ZONE</Typography>
                            </Divider>
                            <MenuItem onClick={() => setEditorMode('audit')} sx={{ color: 'red' }}>
                                Go in audit mode
                            </MenuItem>
                            {editorMode !== 'edit' && (
                                <MenuItem onClick={() => setOpenInfoModal(true)} sx={{ color: 'red' }}>
                                    Exec info
                                </MenuItem>
                            )}
                        </div>
                    )}
                </Menu>
            </div>
        )
    }

    const onCopy = async () => {
        if (editorMode !== 'edit' && !canEdit) return
        if (selectedNodes.length === 0) return
        const duplicatedNodes = FlowUIManager.duplicateNodes(selectedNodes.map(node => node.data.node as INode), flow)
        applyFlowChange(duplicatedNodes)
    }

    const onDelete = async () => {
        if (editorMode !== 'edit' && !canEdit) return
        if (selectedNodes.length === 0) return
        const deletedNodes = FlowUIManager.updateNodes(selectedNodes.map(x => ({ id: x.id, type: 'remove' })), flow)
        applyFlowChange(deletedNodes)
    }

    const onExit = () => {
        if (editorMode === 'compare') {
            setEditorMode('audit')
            return
        }

        if (history.length > 2) {
            const prevHistory = history[history.length - 2]
            dispatch(changeRoute({ route: prevHistory.route, params: prevHistory.params, customRouteName: prevHistory.customRouteName }))
        } else {
            dispatch(changeRoute({ route: 'overview' }))
        }
    }

    return (
        <>
            <ButtonGroup
                variant='outlined'
                sx={{
                    'backgroundColor': 'var(--xy-node-background-color, var(--xy-node-background-color-default))',
                    '& .MuiButtonGroup-grouped.Mui-disabled': { borderColor: Helper.hexToRgbA(theme.palette[color].main, 0.5) },
                    '& .MuiButtonGroup-grouped': { borderColor: Helper.hexToRgbA(theme.palette[color].main, 1) },
                    'borderRadius': 'calc(var(--unit)*3)'
                }}>
                {renderMenu()}
                <div>
                    <Button color={color}>
                        <FlowSettings flow={flow} />
                    </Button>
                </div>
                {(editorMode === 'edit')
                    ? (
                            <>
                                <Tooltip title='Copy' arrow>
                                    <div>
                                        <Button color={color} disabled={!canEdit || nodes.filter(x => x.selected === true).length === 0} onClick={onCopy}>
                                            <ContentCopyRounded />
                                        </Button>
                                    </div>
                                </Tooltip>
                                <Tooltip title='Delete' arrow>
                                    <div>
                                        <Button color={color} disabled={!canEdit || nodes.filter(x => x.selected === true).length === 0} onClick={onDelete}>
                                            <DeleteRounded />
                                        </Button>
                                    </div>
                                </Tooltip>
                                <Tooltip title='Under development' arrow>
                                    <div>
                                        <Button color={color} disabled={!canEdit || nodes.filter(x => x.selected === true).length === 0} onClick={() => execute(selectedNodes.map(x => x.id))}>
                                            {status === 'loading' ? <CircularProgress size={16} /> : <AccountTreeIcon />}
                                        </Button>
                                    </div>
                                </Tooltip>
                                <Tooltip
                                    key={statusCompileFlow === 'loading' ? 'compiling' : 'normal'}
                                    title={statusCompileFlow === 'loading' ? 'Flow is compiling. . .' : 'Compile flow'}
                                    arrow
                                    open={statusCompileFlow === 'loading' ? true : undefined}
                                    disableHoverListener={statusCompileFlow === 'loading' ? true : undefined}>
                                    <div>
                                        <LoadingButton
                                            disabled={isOldLive}
                                            onClick={() => compileFlow(flow.uuid)}
                                            loading={statusCompileFlow === 'loading'}
                                            color={!isCompiled ? 'error' : 'success'}>
                                            <ShapeLineRounded />
                                        </LoadingButton>
                                    </div>
                                </Tooltip>
                            </>
                        )
                    : <div />}
                {editorMode === 'audit'
                    ? (
                            <div>
                                <Tooltip title='Return to edit mode' arrow>
                                    <LoadingButton
                                        loading={getCurrentFlowStatus === 'loading'}
                                        color={color}
                                        onClick={getCurrentFlow}>
                                        <EditIcon />
                                    </LoadingButton>
                                </Tooltip>
                            </div>
                        )
                    : <div />}
                {(editorMode === 'audit' || editorMode === 'compare')
                    ? (
                            <div>
                                <Tooltip title='Return to home' arrow>
                                    <Button color={color} onClick={onExit}>
                                        <LogoutIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                        )
                    : <div />}
                {editorMode === 'compare'
                    ? (
                            <div>
                                <Tooltip title='Open nodes summary comparison' arrow>
                                    <Button color={color} onClick={() => setOpenSummaryComparison(true)}>
                                        <SummarizeIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                        )
                    : <div />}
                <ExecuteFlowButton
                    flow={flow}
                    setFlow={setFlow}
                    execEnv={execEnv}
                    comparedExecEnv={comparedExecEnv}
                    runMode={runMode}
                    color={color}
                    editorMode={editorMode}
                    setRunMode={setRunMode}
                    setEE={setExecEnv}
                    setComparedExecEnv={setComparedExecEnv}
                    setEditorMode={setEditorMode}
                    applyFlowChange={applyFlowChange}
                    flowRunConfig={flowRunConfig}
                    approval={approval}
                />
            </ButtonGroup>
            <QuantumTimelineUI flow={flow} open={openTimelineModal} setOpen={setOpenTimelineModal} setError={setError} />
            {openGlobalsModal && <SetNodeGlobalsModal flow={flow} updateFlow={updateFlow} open={openGlobalsModal} setOpen={setOpenGlobalsModal} statusUpdateFlow={statusUpdateFlow} approval={approval} editorMode={editorMode} />}
            <Modal
                open={openInfoModal}
                onClose={() => setOpenInfoModal(false)}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', width: 'calc(100vw - var(--left-nav-width))', left: 'var(--left-nav-width)' }}>
                <div style={{ flex: 1 }}>
                    <ExecuteStatusModal flow={flow} />
                </div>
            </Modal>
            <Modal
                open={openSummaryComparison}
                onClose={() => setOpenSummaryComparison(false)}
                sx={{ margin: 'auto auto', height: '80vh', width: 'calc(90vw - var(--left-nav-width))', left: 'var(--left-nav-width)' }}>
                <div style={{ flex: 1 }}>
                    <SummaryExecEnvFlowComparison execEnv={execEnv} comparedExecEnv={comparedExecEnv} />
                </div>
            </Modal>
            <Modal
                open={openBatchUpdateModal}
                onClose={() => setOpenBatchUpdateModal(false)}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 'calc(100vw - var(--left-nav-width))', left: 'var(--left-nav-width)' }}>
                <UpdateFlowModal flow={flow} setOpenBatchUpdateModal={setOpenBatchUpdateModal} />
            </Modal>
        </>
    )
}

export default FlowFloatingActionBar
